// BotsPage.tsx — the signed-in account's enrolled agents (Grokbot, Musebot, any kind).
//
// Create and rotate are the only responses that carry an enrollment key.
// The key stays in component state until it is hidden or the page is left.
// A later visit does not invent one. The same holds for each bot's agent
// tokens below the list (AgentTokens.tsx): shown once, on create.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiGet, apiSend } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import type {
  BotCreateRequest,
  BotEnrollmentReceipt,
  BotListResponse,
  BotSummary,
  BotTaskKind,
} from '../../api/types';
import { ActionButton } from '../../components/action/ActionButton';
import { ErrorState, LoadingState } from '../../components/state';
import { When } from '../../format/When';
import { usePageTitle } from '../../hooks/usePageTitle';
import { AgentsTabs } from '../agents/AgentsTabs';
import { AgentTokens } from './AgentTokens';
import { TeamBots } from './TeamBots';
import { BotKindBadge } from './BotKindBadge';
import { BotRefReach } from './BotRefReach';
import { ConnectAgentDoc } from './ConnectAgentDoc';
import {
  GENERAL_REACH_NOTE,
  KIND_DEFAULT_NOTE,
  KIND_SUGGESTIONS,
  REACH_LINES_HINT,
  REACH_LINES_LABEL,
  SETUP_WARNING,
  accountReachRows,
  isReachChoice,
  isTaskKind,
  oneTaskChoiceLabel,
  parseKind,
  parseOwnerName,
  reachFromChoice,
  statusLabel,
  taskIdFieldLabel,
  taskKindFieldLabel,
  type ReachChoice,
} from './botModel';

import '../page.css';
import './BotsPage.css';

const ACCOUNT_BOTS_KEY = ['account-bots'] as const;

function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `bot-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function upsertBot(
  queryClient: ReturnType<typeof useQueryClient>,
  bot: BotSummary
): void {
  queryClient.setQueryData<BotListResponse>(ACCOUNT_BOTS_KEY, (current) => {
    const bots = current?.bots ?? [];
    const exists = bots.some((row) => row.id === bot.id);
    return {
      ...current,
      bots: exists ? bots.map((row) => (row.id === bot.id ? bot : row)) : [bot, ...bots],
    };
  });
}

export function BotsPage(): JSX.Element {
  usePageTitle('Agents · Enrolled');
  const [params] = useSearchParams();
  const presetRepo = params.get('repo');
  const queryClient = useQueryClient();
  const [receipt, setReceipt] = useState<BotEnrollmentReceipt | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const list = useQuery({
    queryKey: ACCOUNT_BOTS_KEY,
    queryFn: ({ signal }) => apiGet<BotListResponse>(endpoints.bots(), { signal }),
    staleTime: 10_000,
    retry: false,
  });

  const create = useMutation({
    mutationFn: (body: BotCreateRequest) =>
      apiSend<BotEnrollmentReceipt>(endpoints.bots(), body, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (created) => {
      setReceipt(created);
      setActionError(null);
      upsertBot(queryClient, created.bot);
      void queryClient.invalidateQueries({ queryKey: ACCOUNT_BOTS_KEY });
    },
  });

  const rotate = useMutation({
    mutationFn: (id: string) =>
      apiSend<BotEnrollmentReceipt>(endpoints.botKeys(id), undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    onSuccess: (rotated) => {
      setReceipt(rotated);
      setActionError(null);
      upsertBot(queryClient, rotated.bot);
      void queryClient.invalidateQueries({ queryKey: ACCOUNT_BOTS_KEY });
    },
    onError: (error) => setActionError(error),
  });

  const suspend = useMutation({
    mutationFn: (id: string) => apiSend<BotSummary>(endpoints.botSuspend(id)),
    onSuccess: (bot) => {
      setActionError(null);
      upsertBot(queryClient, bot);
      void queryClient.invalidateQueries({ queryKey: ACCOUNT_BOTS_KEY });
    },
    onError: (error) => setActionError(error),
  });

  const revoke = useMutation({
    mutationFn: (id: string) => apiSend<BotSummary>(endpoints.botRevoke(id)),
    onSuccess: (bot) => {
      setActionError(null);
      upsertBot(queryClient, bot);
      void queryClient.invalidateQueries({ queryKey: ACCOUNT_BOTS_KEY });
    },
    onError: (error) => setActionError(error),
  });

  function hideReceipt(): void {
    setReceipt(null);
    create.reset();
    rotate.reset();
  }

  function busy(id: string): boolean {
    return (
      (suspend.isPending && suspend.variables === id) ||
      (revoke.isPending && revoke.variables === id) ||
      (rotate.isPending && rotate.variables === id)
    );
  }

  const bots = list.data?.bots ?? [];
  const teamBots = list.data?.team_bots;

  return (
    <div className="page" data-testid="enroll-page">
      <header className="page__header">
        <h1 className="page__title">Agents</h1>
      </header>
      <AgentsTabs current="enrolled" />

      {receipt ? (
        <section className="page__card enroll-setup" data-testid="bot-setup" aria-live="polite">
          <h2 className="page__card-title">Enrollment</h2>
          <p className="enroll-form__note" data-testid="bot-setup-warning">
            {SETUP_WARNING}
          </p>
          <h3 id="bot-key-label">Enrollment key</h3>
          <pre className="enroll-setup__secret" aria-labelledby="bot-key-label">
            {receipt.enrollment_key}
          </pre>
          <h3 id="bot-script-label">Setup script</h3>
          <pre className="enroll-setup__secret" aria-labelledby="bot-script-label">
            {receipt.setup_script}
          </pre>
          <div className="page__inline-actions">
            <ActionButton type="button" onClick={hideReceipt}>
              Hide this key
            </ActionButton>
          </div>
        </section>
      ) : null}

      <BotForm
        key={presetRepo ?? ''}
        preset={parseOwnerName(presetRepo)}
        pending={create.isPending}
        createError={create.error}
        onCreate={(body) => create.mutate(body)}
      />

      <section className="page__section" aria-labelledby="account-bots" data-testid="bot-list">
        <h2 className="page__section-title" id="account-bots">
          On this account
        </h2>
        {actionError ? (
          <ErrorState title="Could not update it." error={actionError} testId="bot-action-error" />
        ) : null}
        {list.isPending ? (
          <LoadingState variant="message" title="Loading agents." />
        ) : list.isError ? (
          <ErrorState
            title="Could not load agents."
            error={list.error}
            onRetry={() => void list.refetch()}
            testId="bot-list-error"
          />
        ) : bots.length === 0 ? (
          <p className="enroll-form__note">No agent on this account yet.</p>
        ) : (
          <div className="enroll-table-wrap table-scroll">
            <table className="enroll-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Kind</th>
                  <th scope="col">Reach</th>
                  <th scope="col">Status</th>
                  <th scope="col">Last successful access</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {bots.map((enrollment) => {
                  const revoked = enrollment.status === 'revoked';
                  const rowBusy = busy(enrollment.id);
                  return (
                    <tr key={enrollment.id}>
                      <th scope="row">
                        <span className="enroll-table__name">{enrollment.display_name}</span>
                        <span className="enroll-table__slug">{enrollment.slug}</span>
                      </th>
                      <td>
                        <BotKindBadge kind={enrollment.kind} />
                      </td>
                      <td>
                        <ul className="enroll-reach" data-testid="bot-reach">
                          {accountReachRows(enrollment.reach).map((row) => (
                            <li key={row} data-testid="bot-reach-row">
                              {row}
                            </li>
                          ))}
                        </ul>
                        <BotRefReach reach={enrollment.reach} />
                      </td>
                      <td>{statusLabel(enrollment.status)}</td>
                      <td>
                        <When
                          at={enrollment.last_successful_access}
                          label="Last successful access"
                        />
                      </td>
                      <td className="enroll-table__actions">
                        <div className="enroll-actions">
                          <ActionButton
                            type="button"
                            data-testid="bot-suspend"
                            aria-label={`Suspend ${enrollment.display_name}`}
                            disabled={rowBusy || enrollment.status !== 'active'}
                            onClick={() => suspend.mutate(enrollment.id)}
                          >
                            Suspend
                          </ActionButton>
                          <ActionButton
                            type="button"
                            variant="human"
                            data-testid="bot-revoke"
                            aria-label={`Revoke ${enrollment.display_name}`}
                            disabled={rowBusy || revoked}
                            onClick={() => revoke.mutate(enrollment.id)}
                          >
                            Revoke
                          </ActionButton>
                          <ActionButton
                            type="button"
                            data-testid="bot-rotate"
                            aria-label={`Rotate ${enrollment.display_name}`}
                            disabled={rowBusy || revoked}
                            onClick={() => rotate.mutate(enrollment.id)}
                          >
                            Rotate
                          </ActionButton>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {bots.length > 0 ? (
        <section
          className="page__section agent-tokens-section"
          aria-labelledby="agent-tokens"
          data-testid="agent-tokens-section"
        >
          <h2 className="page__section-title" id="agent-tokens">
            Agent tokens
          </h2>
          <p className="enroll-form__note">
            An agent token connects an agent with one URL and one token, on MCP, the REST API,
            gh and git. It acts for one agent: your rights, cut down to its reach. The token is
            shown once, when you create it.
          </p>
          <ConnectAgentDoc testId="bots-connect-doc" />
          {bots.map((enrollment) => (
            <AgentTokens key={enrollment.id} bot={enrollment} />
          ))}
        </section>
      ) : null}

      {/* An older server sends no team_bots: say nothing about team agents. */}
      {teamBots !== undefined ? (
        <TeamBots
          bots={teamBots}
          onChanged={() => void queryClient.invalidateQueries({ queryKey: ACCOUNT_BOTS_KEY })}
        />
      ) : null}
    </div>
  );
}

function BotForm({
  preset,
  pending,
  createError,
  onCreate,
}: {
  preset: ReturnType<typeof parseOwnerName>;
  pending: boolean;
  createError: unknown;
  onCreate: (body: BotCreateRequest) => void;
}): JSX.Element {
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [kind, setKind] = useState('');
  const [choice, setChoice] = useState<ReachChoice>(preset ? 'repositories' : 'general');
  const [repoLines, setRepoLines] = useState(
    preset ? `${preset.owner}/${preset.name}` : ''
  );
  const [taskOwner, setTaskOwner] = useState(preset?.owner ?? '');
  const [taskName, setTaskName] = useState(preset?.name ?? '');
  const [taskKind, setTaskKind] = useState<BotTaskKind>('issue');
  const [taskId, setTaskId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const display_name = displayName.trim();
    const slugValue = slug.trim();
    if (display_name === '' || slugValue === '') {
      setFormError('Enter a display name and a slug.');
      return;
    }
    const parsedKind = parseKind(kind);
    if (parsedKind.error !== null) {
      setFormError(parsedKind.error);
      return;
    }
    const built = reachFromChoice({
      choice,
      repoLines,
      taskOwner,
      taskName,
      taskKind,
      taskId,
    });
    if (built.reach === null) {
      setFormError(built.error);
      return;
    }
    setFormError(null);
    const body: BotCreateRequest = { slug: slugValue, display_name, reach: built.reach };
    if (parsedKind.kind !== null) body.kind = parsedKind.kind;
    onCreate(body);
  }

  return (
    <form className="page__card enroll-form" data-testid="bot-form" onSubmit={onSubmit}>
      <h2 className="page__card-title">Add an agent</h2>
      <div className="enroll-form__grid">
        <label className="enroll-form__field">
          Display name
          <input
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            autoComplete="off"
            disabled={pending}
          />
        </label>
        <label className="enroll-form__field">
          Slug
          <input
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            disabled={pending}
          />
        </label>
        <div className="enroll-form__stack">
          <label className="enroll-form__field">
            Kind (optional)
            <input
              data-testid="bot-kind-input"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              list="bot-kind-suggestions"
              placeholder="agent"
              aria-describedby="bot-kind-note"
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              maxLength={32}
              disabled={pending}
            />
          </label>
          <datalist id="bot-kind-suggestions">
            {KIND_SUGGESTIONS.map((suggestion) => (
              <option key={suggestion} value={suggestion} />
            ))}
          </datalist>
          <span className="enroll-form__hint" id="bot-kind-note">
            {KIND_DEFAULT_NOTE}
          </span>
        </div>
        <label className="enroll-form__field">
          Reach
          <select
            data-testid="bot-reach-select"
            value={choice}
            disabled={pending}
            onChange={(event) => {
              if (isReachChoice(event.target.value)) setChoice(event.target.value);
            }}
          >
            <option value="general">General</option>
            <option value="repositories">Repositories or owners</option>
            <option value="task">{oneTaskChoiceLabel()}</option>
          </select>
        </label>
        {choice === 'repositories' ? (
          <div className="enroll-form__stack enroll-form__span">
            <label className="enroll-form__field">
              {REACH_LINES_LABEL}
              <textarea
                rows={4}
                value={repoLines}
                onChange={(event) => setRepoLines(event.target.value)}
                placeholder={'acme/widgets\nacme/gadgets'}
                aria-describedby="bot-reach-lines-note"
                data-testid="bot-reach-lines"
                spellCheck={false}
                disabled={pending}
              />
            </label>
            <span className="enroll-form__hint" id="bot-reach-lines-note">
              {REACH_LINES_HINT}
            </span>
          </div>
        ) : null}
        {choice === 'task' ? (
          <>
            <label className="enroll-form__field">
              Owner
              <input
                value={taskOwner}
                onChange={(event) => setTaskOwner(event.target.value)}
                autoComplete="off"
                disabled={pending}
              />
            </label>
            <label className="enroll-form__field">
              Name
              <input
                value={taskName}
                onChange={(event) => setTaskName(event.target.value)}
                autoComplete="off"
                disabled={pending}
              />
            </label>
            <label className="enroll-form__field">
              {taskKindFieldLabel()}
              <select
                value={taskKind}
                disabled={pending}
                onChange={(event) => {
                  if (isTaskKind(event.target.value)) setTaskKind(event.target.value);
                }}
              >
                <option value="issue">Issue</option>
                <option value="pull">Pull request</option>
                <option value="branch">Branch</option>
              </select>
            </label>
            <label className="enroll-form__field">
              {taskIdFieldLabel()}
              <input
                value={taskId}
                onChange={(event) => setTaskId(event.target.value)}
                autoComplete="off"
                spellCheck={false}
                disabled={pending}
              />
            </label>
          </>
        ) : null}
      </div>
      <p className="enroll-form__note">{GENERAL_REACH_NOTE}</p>
      {formError ? (
        <p className="enroll-form__error" role="alert">
          {formError}
        </p>
      ) : null}
      {createError ? (
        <ErrorState title="Could not add it." error={createError} testId="bot-form-error" />
      ) : null}
      <div className="page__inline-actions">
        <ActionButton type="submit" variant="primary" disabled={pending}>
          {pending ? 'Adding…' : 'Add'}
        </ActionButton>
      </div>
    </form>
  );
}
